import { io } from 'socket.io-client';
import { WS_URL } from '../config/api.config.js';
import logger from '@/utils/logger';
import { WEBSOCKET } from '../config/constants';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = WEBSOCKET.MAX_RECONNECT_ATTEMPTS;
    this.reconnectInterval = WEBSOCKET.RECONNECT_INTERVAL_MS;
    this.listeners = new Map();
    this.baseURL = WS_URL;
    // Compteur de référence : plusieurs consommateurs (page GPS suivi ET
    // badge notifications du Layout) partagent la même connexion socket.io.
    // Sans ce compteur, le disconnect() d'un consommateur au démontage
    // couperait la connexion encore utilisée par l'autre.
    this.refCount = 0;

    logger.debug('🔌 WebSocket Service: Initialisation', this.baseURL);
  }

  connect() {
    this.refCount += 1;
    if (this.socket) {
      return;
    }
    this._open();
  }

  // Établit réellement la connexion socket.io. Séparé de connect() pour que
  // la reconnexion automatique interne (ci-dessous) ne fasse pas grimper
  // refCount à chaque tentative — seul un appel externe à connect() doit
  // compter comme un nouveau consommateur.
  _open() {
    try {
      logger.debug('🔌 WebSocket Service: Tentative de connexion...', this.baseURL);

      this.socket = io(this.baseURL, {
        transports: ['websocket', 'polling'],
        timeout: WEBSOCKET.SOCKET_TIMEOUT_MS,
        forceNew: true,
      });

      this.socket.on('connect', () => {
        logger.debug('✅ WebSocket Service: Connexion établie');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.socket.emit('join_room', 'gps_all');
        logger.debug('📡 WebSocket Service: Abonnement aux mises à jour GPS');
      });

      this.socket.on('disconnect', (reason) => {
        logger.debug('❌ WebSocket Service: Déconnexion', reason);
        this.isConnected = false;

        // Reconnexion automatique limitée à maxReconnectAttempts pour éviter une boucle infinie
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          setTimeout(() => {
            this.reconnectAttempts++;
            logger.debug(
              `🔄 WebSocket Service: Tentative de reconnexion ${this.reconnectAttempts}/${this.maxReconnectAttempts}`,
            );
            this._open();
          }, this.reconnectInterval);
        }
      });

      this.socket.on('connect_error', (error) => {
        logger.error('❌ WebSocket Service: Erreur de connexion', error);
        this.isConnected = false;
      });

      // Accusé de réception serveur (config/socket.js::handleJoinRoom) — confirmation
      // sans ambiguïté que le join a bien été traité côté backend, pas juste émis côté client.
      this.socket.on('room_joined', (data) => {
        logger.debug(`✅ WebSocket Service: Room rejointe (confirmé par le serveur) — ${data?.room}`);
      });

      this.socket.on('gps_update', (data) => {
        logger.debug('📍 WebSocket Service: Mise à jour GPS reçue', data);
        this.notifyListeners('gps_update', data);
      });

      this.socket.on('gps_stats', (data) => {
        logger.debug('📊 WebSocket Service: Statistiques GPS reçues', data);
        this.notifyListeners('gps_stats', data);
      });

      this.socket.on('admin_notification', (data) => {
        logger.debug('🔔 WebSocket Service: Notification admin reçue', data);
        this.notifyListeners('admin_notification', data);
      });
    } catch (error) {
      logger.error('❌ WebSocket Service: Erreur lors de la connexion', error);
      this.isConnected = false;
    }
  }

  // Rejoint la room school:{schoolId} — diffusée par adminNotificationService
  // (backend) pour le badge/toast temps réel. À appeler après connect().
  //
  // IMPORTANT : config/socket.js::handleJoinRoom (backend) attend un objet
  // { room }, pas une chaîne brute — const { room } = data sur une string
  // donne room=undefined, retour anticipé silencieux (socket.join(room)
  // jamais appelé). Bug confirmé en test (0 clients au broadcast malgré une
  // connexion WS établie et un emit qui s'exécute bien côté client).
  joinSchoolRoom(schoolId) {
    if (!schoolId) {
      return;
    }
    const room = `school:${schoolId}`;
    if (this.socket && this.isConnected) {
      logger.debug(`📡 WebSocket Service: Rejoint la room ${room} (déjà connecté)`);
      this.socket.emit('join_room', { room });
    } else {
      logger.debug(`📡 WebSocket Service: Rejoindra ${room} dès la connexion (en attente)`);
      this.socket?.once('connect', () => {
        logger.debug(`📡 WebSocket Service: Rejoint la room ${room} (après connexion différée)`);
        this.socket.emit('join_room', { room });
      });
    }
  }

  disconnect() {
    this.refCount = Math.max(0, this.refCount - 1);
    if (this.refCount > 0) {
      return;
    }
    if (this.socket) {
      logger.debug('🔌 WebSocket Service: Fermeture de la connexion');
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  addListener(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    this.listeners.get(eventType).push(callback);
    logger.debug(`📡 WebSocket Service: Listener ajouté pour ${eventType}`);
  }

  removeListener(eventType, callback) {
    if (this.listeners.has(eventType)) {
      const callbacks = this.listeners.get(eventType);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        logger.debug(`📡 WebSocket Service: Listener supprimé pour ${eventType}`);
      }
    }
  }

  notifyListeners(eventType, data) {
    if (this.listeners.has(eventType)) {
      this.listeners.get(eventType).forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          logger.error(`❌ WebSocket Service: Erreur dans le listener ${eventType}`, error);
        }
      });
    }
  }

  emit(eventType, data) {
    if (this.socket && this.isConnected) {
      this.socket.emit(eventType, data);
      logger.debug(`📤 WebSocket Service: Message envoyé ${eventType}`, data);
    } else {
      logger.warn("⚠️ WebSocket Service: Impossible d'envoyer le message - Non connecté");
    }
  }

  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
    };
  }

  forceReconnect() {
    logger.debug('🔄 WebSocket Service: Reconnexion forcée');
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
    this.reconnectAttempts = 0;
    setTimeout(() => this._open(), 1000);
  }
}

export default new WebSocketService();
